import { EMPTY_FIELD } from 'constants/emptyField';
import { UNLIMITED_PAGINATION_PARAMS } from 'constants/pagination';
import { Resources } from 'constants/resources';

import { useMemo } from 'react';

import { FormControl } from '@mui/material';
import { AxiosResponse } from 'axios';
import { updateProviderAccountWorkTeam } from 'config/requests/providerAccounts';
import { queryClient } from 'providers/queryClient';
import { useGetList, useNotify } from 'react-admin';
import { Controller, useForm } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { Button, CancelButton } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { Dialog, DialogProps } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { MenuItem } from 'shared/mui/MenuItem/MenuItem';
import { Select } from 'shared/mui/Select/Select';
import { Typography } from 'shared/mui/Typography';
import { Country } from 'types/embedded/country';
import { ProviderAccount } from 'types/embedded/providerAccount';
import { WorkTeam } from 'types/embedded/workTeam';

import { StyledBox, StyledCardContent, StyledInputLabel } from './WorkTeamAddDialog.styled';

type WorkGroupAddDialogProps = {
  provider?: { providerAccount: ProviderAccount };
  onClose: () => void;
} & DialogProps;

export const WorkTeamAddDialog = ({ open, provider, onClose }: WorkGroupAddDialogProps) => {
  const notify = useNotify();
  const { control, handleSubmit, getValues } = useForm({
    defaultValues: {
      provider: provider?.providerAccount?.id,
      workTeam: '',
    },
  });

  const { data: workTeams } = useGetList<WorkTeam>(Resources.WorkTeams, {
    pagination: UNLIMITED_PAGINATION_PARAMS,
  });

  const { data: countries } = useGetList<Country>('countries', {
    pagination: UNLIMITED_PAGINATION_PARAMS,
  });

  const workTeamsConfig = useMemo(
    () =>
      workTeams?.reduce<Record<string, WorkTeam>>((acc, workTeam) => {
        acc[workTeam.id] = workTeam;

        return acc;
      }, {}),
    [workTeams?.length],
  );
  const countriesConfig = useMemo(
    () =>
      countries?.reduce<Record<string, string>>((acc, { id, name }) => {
        acc[id] = name;

        return acc;
      }, {}),
    [countries?.length],
  );

  const onSubmit = async () => {
    if (provider?.providerAccount.id) {
      try {
        await updateProviderAccountWorkTeam(
          provider.providerAccount.originId,
          getValues().workTeam,
        );
        await queryClient.invalidateQueries({ queryKey: [Resources.ProviderAccounts] });
        onClose();
      } catch {
        (err: AxiosResponse) => {
          notify(err?.data?.detail, { type: 'error' });
        };
      }
    }
  };

  if (!workTeamsConfig || !countriesConfig) {
    return <></>;
  }

  return (
    <Dialog onClose={onClose} open={open} width="640px">
      <StyledBox>
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title="Назначить рабочую группу"
          titleSize="smallBold"
        />
        <StyledCardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              control={control}
              name="workTeam"
              render={({ field }) => (
                <FormControl fullWidth variant="outlined">
                  <StyledInputLabel id="work-team" shrink>
                    Рабочая группа
                  </StyledInputLabel>
                  <Select
                    label="Рабочая группа"
                    labelId="work-team"
                    onChange={field.onChange}
                    renderValue={(selected: string) => workTeamsConfig[selected]?.name}
                    size="small"
                    value={field?.value}
                  >
                    {workTeams?.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography component="p" variant="body2">
                            {item?.name}
                          </Typography>
                          <Typography
                            color="secondary"
                            component="p"
                            sx={{
                              width: 'calc(600px - 48px * 2)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            variant="body2"
                          >
                            {item.countries && countriesConfig
                              ? item.countries.map((id) => countriesConfig[id]).join(', ')
                              : EMPTY_FIELD}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </form>
        </StyledCardContent>
        <CardActionsButtons
          rightActionsSlot={[
            <CancelButton key="cancel-button" onClick={onClose} variant="outlined" />,
            <Button key="save-button" label="Сохранить" onClick={onSubmit} variant="contained" />,
          ]}
        />
      </StyledBox>
    </Dialog>
  );
};
